// src/App.jsx
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';

import { motion } from 'framer-motion';
import { Package, MapPin, Phone, PlayCircle, User, Info, Upload, FileSpreadsheet, Truck, Settings, ClipboardList, Banknote, Users, BarChart3, WalletCards, ShieldCheck, Link as LinkIcon } from 'lucide-react';

import { useTelegram } from './hooks/useTelegram';
import AgreementScreen from './components/AgreementScreen';
import OrdersPage from './pages/OrdersPage';
import AddressesPage from './pages/AddressesPage';
import ContactsPage from './pages/ContactsPage';
import VideosPage from './pages/VideosPage';
import ProfilePage from './pages/ProfilePage';
import AboutPage from './pages/AboutPage';
import DeliveryPage from './pages/DeliveryPage';
import CourierPage from './pages/CourierPage';
import ManagerDeliveryPage from './pages/ManagerDeliveryPage';
import ManagerClientTracksPage from './pages/ManagerClientTracksPage';
import CashierPage from './pages/CashierPage';
import PickupKioskPage from './pages/PickupKioskPage';
import AdminCashiersPage from './pages/AdminCashiersPage';
import AdminStaffPage from './pages/AdminStaffPage';
import AdminLinksPage from './pages/AdminLinksPage';
import ManagerReportsPage from './pages/ManagerReportsPage';
import ManagerReconciliationPage from './pages/ManagerReconciliationPage';

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.error || `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return data;
};

const digitsOnly = (value) => (value || '').replace(/\D/g, '');

const formatPhoneInput = (value) => {
  const digits = digitsOnly(value).slice(0, 12);
  return `+${digits}`;
};

const maskPhone = (raw) => {
  const d = digitsOnly(raw).slice(0, 12);
  if (!d) return '';

  if (d.startsWith('7')) {
    const a = d.slice(0, 1);
    const b = d.slice(1, 4);
    const c = d.slice(4, 7);
    const e = d.slice(7, 9);
    const f = d.slice(9, 11);
    let out = `+${a}`;
    if (b) out += ` ${b}`;
    if (c) out += ` ${c}`;
    if (e) out += ` ${e}`;
    if (f) out += ` ${f}`;
    return out;
  }

  if (d.startsWith('996')) {
    const a = d.slice(0, 3);
    const b = d.slice(3, 6);
    const c = d.slice(6, 9);
    const e = d.slice(9, 12);
    let out = `+${a}`;
    if (b) out += ` ${b}`;
    if (c) out += ` ${c}`;
    if (e) out += ` ${e}`;
    return out;
  }

  if (d.startsWith('81')) {
    const a = d.slice(0, 2);
    const b = d.slice(2, 4);
    const c = d.slice(4, 8);
    const e = d.slice(8, 12);
    let out = `+${a}`;
    if (b) out += ` ${b}`;
    if (c) out += ` ${c}`;
    if (e) out += ` ${e}`;
    return out;
  }

  return `+${d}`;
};

const LS_LAST_ROUTE_KEY = 'japanexpress_last_route_v1';
const LS_FULL_NAME_KEY = 'japanexpress_full_name_v1';
const LS_ADDRESS_KEY = 'japanexpress_address_v1';

const readCachedFullName = () => {
  try {
    return String(localStorage.getItem(LS_FULL_NAME_KEY) || '').trim();
  } catch (_) {
    return '';
  }
};

const readCachedAddress = () => {
  try {
    return String(localStorage.getItem(LS_ADDRESS_KEY) || '').trim();
  } catch (_) {
    return '';
  }
};

const normalizeRoute = (value) => {
  if (!value || typeof value !== 'string') return '';
  let v = value.trim();
  if (!v) return '';

  try {
    if (/^https?:\/\//i.test(v)) v = new URL(v).pathname || v;
  } catch (_) {
    // ignore
  }

  if (v.startsWith('/japanexpress/')) v = v.replace('/japanexpress', '');
  if (v === '/japanexpress') v = '/';

  if (!v.startsWith('/')) v = `/${v}`;
  return v;
};

const readLastRoute = () => {
  const fallback = '/orders';

  try {
    const v = normalizeRoute(sessionStorage.getItem(LS_LAST_ROUTE_KEY));
    if (v) return v;
  } catch (_) {
    // ignore
  }

  try {
    const v = normalizeRoute(localStorage.getItem(LS_LAST_ROUTE_KEY));
    if (v) return v;
  } catch (_) {
    // ignore
  }

  return fallback;
};

const InnRequiredScreen = ({ initialInn, onSubmit }) => {
  const [inn, setInn] = React.useState(digitsOnly(initialInn || '').slice(0, 14));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleSave = async () => {
    const normalized = digitsOnly(inn).slice(0, 14);
    if (!normalized) {
      setError('Введите ИНН');
      return;
    }

    if (normalized.length < 12) {
      setError('ИНН должен содержать минимум 12 цифр');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await onSubmit(normalized);
    } catch (e) {
      setError(e?.message || 'Ошибка сохранения');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg text-text-primary flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-stroke bg-surface p-6 backdrop-blur-md">
        <h1 className="text-xl font-semibold tracking-tight">Введите ваш ИНН</h1>
        <p className="mt-2 text-sm text-text-secondary">
          Введите ваш ИНН с паспорта для работы с растаможкой.
        </p>

        <div className="mt-5">
          <input
            value={inn}
            onChange={(e) => setInn(digitsOnly(e.target.value).slice(0, 14))}
            placeholder="12345678901234"
            className="w-full rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none focus:border-white/25"
            inputMode="numeric"
            autoComplete="off"
          />
          {error ? <div className="mt-2 text-xs text-red-300">{error}</div> : null}
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={handleSave}
          className="mt-5 w-full py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15] text-white hover:brightness-110 transition disabled:opacity-60"
        >
          {loading ? 'Сохранение…' : 'Продолжить'}
        </button>
      </div>
    </div>
  );
};

const TelegramOnlyScreen = () => {
  return (
    <div className="min-h-screen bg-bg text-text-primary flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-stroke bg-surface p-6 text-center backdrop-blur-md">
        <div className="mx-auto mb-4 h-12 w-12 rounded-2xl bg-white/5 border border-stroke flex items-center justify-center shadow-glow">
          <span className="text-neon-blue font-semibold">JE</span>
        </div>
        <h1 className="text-xl font-semibold tracking-tight">Откройте miniapp в Telegram</h1>
        <p className="mt-2 text-sm text-text-secondary">Эта страница работает только внутри Telegram.</p>
      </div>
    </div>
  );
};

const Tile = ({ title, icon, description, path, featured = false, attention = false }) => {
  const { tg } = useTelegram();
  const navigate = useNavigate();

  const handleClick = () => {
    if (tg?.HapticFeedback?.impactOccurred) {
      tg.HapticFeedback.impactOccurred('light');
    }
    navigate(path);
  };

  return (
    <motion.div
      initial={false}
      whileHover={{ scale: featured ? 1.02 : 1.01 }}
      whileTap={{ scale: 0.96 }}
      onClick={handleClick}
      className={`group relative overflow-hidden rounded-2xl p-3 flex flex-col items-center justify-center text-center cursor-pointer border border-stroke backdrop-blur-md transition-all duration-200 hover:bg-surface-hover hover:border-white/20 ${
        featured ? 'bg-gradient-to-br from-neon-yellow/10 via-white/3 to-neon-blue/8' : 'bg-surface'
      }`}
    >
      {attention ? (
        <div className="absolute top-3 right-3 h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.18)]" />
      ) : null}
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />

      {!featured ? (
        <div
          className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-200 group-hover:opacity-100 shadow-[0_0_0_1px_rgba(250,204,21,0.35),0_0_24px_rgba(59,130,246,0.18)]"
        />
      ) : null}

      <div className="relative mb-2">
        <div
          className="h-10 w-10 rounded-2xl border border-stroke bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center shadow-[0_18px_50px_rgba(59,130,246,0.16)]"
        >
          <div
            className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-b from-white/10 to-transparent"
          />
          <div
            className={`${featured ? 'text-neon-yellow' : 'text-neon-blue'} drop-shadow-[0_10px_18px_rgba(59,130,246,0.25)]`}
          >
            {React.cloneElement(icon, { size: 24 })}
          </div>
        </div>
      </div>

      <h3 className="font-semibold text-sm mb-0.5 text-text-primary">{title}</h3>
      {description && <p className="text-sm text-text-secondary">{description}</p>}
    </motion.div>
  );
};

const Home = ({ featuredPath, needsPhone, isAdmin, isManager, isCourier, userCountry }) => {
  const { user } = useTelegram();

  const tiles = [
    { title: 'Мои заказы', icon: <Package />, description: '', path: '/orders' },
    { title: 'Реквизиты и адреса', icon: <MapPin />, description: '', path: '/addresses' },
    { title: 'Контакты', icon: <Phone />, path: '/contacts' },
    { title: 'Видео Материалы', icon: <PlayCircle />, path: '/videos' },
    { title: 'Профиль', icon: <User />, path: '/profile', attention: needsPhone },
    { title: 'FAQ', icon: <Info />, path: '/about' },
    { title: 'Доставка', icon: <Truck />, path: '/delivery' },
    ...(isCourier ? [{ title: 'Курьер', icon: <Truck />, path: '/courier' }] : []),
    ...(isManager || isAdmin ? [{ title: 'Кассир', icon: <Banknote />, path: '/cashier' }] : []),
    ...(isManager ? [{ title: 'Управление доставкой', icon: <Settings />, path: '/manager/delivery' }] : []),
    ...(isManager ? [{ title: 'Треки от клиентов', icon: <ClipboardList />, path: '/manager/client-tracks' }] : []),
    ...(isManager || isAdmin ? [{ title: 'Отчёты', icon: <BarChart3 />, path: '/manager/reports' }] : []),
    ...(isManager || isAdmin ? [{ title: 'Сверка с банком', icon: <WalletCards />, path: '/manager/reconciliation' }] : []),
    ...(isAdmin ? [{ title: 'Сотрудники и роли', icon: <ShieldCheck />, path: '/admin/staff' }] : []),
    ...(isAdmin ? [{ title: 'Админка кассиров', icon: <Users />, path: '/admin/cashiers' }] : []),
    ...(isAdmin ? [{ title: 'Ссылки для работы', icon: <LinkIcon />, path: '/admin/links' }] : []),
    ...(isAdmin ? [{ title: 'Импорт Excel', icon: <FileSpreadsheet />, path: '/admin/import' }] : []),
  ].map((t) => ({ ...t, featured: t.path === (featuredPath || '/orders') }));

  return (
    <div className="min-h-screen bg-bg text-text-primary pb-16 relative overflow-hidden">
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-neon-blue/15 blur-3xl" />
      <div className="absolute -top-10 -right-28 h-80 w-80 rounded-full bg-neon-yellow/10 blur-3xl" />
      <div className="absolute bottom-[-160px] left-1/2 -translate-x-1/2 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="pt-6 pb-8 flex flex-col items-center px-4 relative">
        <div
          className="mb-4 h-16 w-16 rounded-2xl bg-white/5 border border-stroke flex items-center justify-center shadow-glow shadow-[0_18px_55px_rgba(59,130,246,0.28)]"
        >
          <img src="/logo.png" alt="Japan Express" className="w-11 h-11 object-contain" />
        </div>

        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-center leading-tight">
          <span className="text-text-primary">Добро пожаловать в</span>
          <br />
          <span className="text-neon-yellow drop-shadow-[0_0_22px_rgba(250,204,21,0.22)]">
            Japan Express
          </span>
        </h1>
        <p className="mt-2 text-sm text-text-secondary text-center">
          {user?.username ? `@${user.username}` : user?.first_name ? user.first_name : ''}
        </p>
      </div>

      <div className="px-4 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-4xl mx-auto relative">
        {tiles.map((tile) => (
          <Tile key={tile.title} {...tile} />
        ))}
      </div>
    </div>
  );
};

const PhoneRequiredScreen = ({ initialPhone, onSubmit }) => {
  const [phone, setPhone] = React.useState(maskPhone(initialPhone || ''));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const { tg } = useTelegram();

  React.useEffect(() => {
    if (!tg?.onEvent) return;

    const handler = (data) => {
      try {
        console.log('[PHONE][SHARE] contactRequested event:', data);
      } catch (_) {
        // ignore
      }

      let receivedRaw = data?.responseUnsafe?.contact?.phone_number || data?.responseUnsafe?.contact?.phoneNumber || '';

      if (!receivedRaw && typeof data?.response === 'string' && data.response) {
        try {
          const params = new URLSearchParams(data.response);
          const contactStr = params.get('contact');
          if (contactStr) {
            const decoded = decodeURIComponent(contactStr);
            const parsed = JSON.parse(decoded);
            receivedRaw = parsed?.phone_number || parsed?.phoneNumber || '';
          }
        } catch (_) {
          // ignore
        }
      }

      const normalized = digitsOnly(receivedRaw);
      if (!normalized) {
        setError('Telegram передал данные, но номер не найден');
        return;
      }

      try {
        setPhone(maskPhone(receivedRaw));
      } catch (_) {
        // ignore
      }

      setLoading(true);
      Promise.resolve()
        .then(() => onSubmit(normalized))
        .catch((e) => {
          setError(e?.message || 'Ошибка сохранения');
          setLoading(false);
        });
    };

    tg.onEvent('contactRequested', handler);
    return () => {
      try {
        tg.offEvent('contactRequested', handler);
      } catch (_) {
        // ignore
      }
    };
  }, [onSubmit, tg]);

  const handleSave = async () => {
    const normalized = digitsOnly(phone).slice(0, 12);
    if (!normalized || normalized.length < 8) {
      setError('Введите корректный номер телефона');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await onSubmit(normalized);
    } catch (e) {
      setError(e?.message || 'Ошибка сохранения');
      setLoading(false);
    }
  };

  const handleShare = () => {
    setError('');

    try {
      console.log('[PHONE][SHARE] requestContact available:', !!tg?.requestContact);
    } catch (_) {
      // ignore
    }

    if (!tg?.requestContact) {
      setError('Функция недоступна. Введите номер вручную.');
      return;
    }

    try {
      tg.requestContact();
    } catch (e) {
      setError('Не удалось запросить контакт. Введите номер вручную.');
    }
  };

  return (
    <div className="min-h-screen bg-bg text-text-primary flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-stroke bg-surface p-6 backdrop-blur-md">
        <h1 className="text-xl font-semibold tracking-tight">Введите свой Whatsapp номер для регистрации</h1>
        <p className="mt-2 text-sm text-text-secondary">Он нужен для связи по вашим заказам.</p>

        <div className="mt-5">
          <input
            value={phone}
            onChange={(e) => setPhone(maskPhone(e.target.value))}
            placeholder="+996 555 123 456"
            className="w-full rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none focus:border-white/25"
            inputMode="tel"
            autoComplete="tel"
          />
          {error ? <div className="mt-2 text-xs text-red-300">{error}</div> : null}
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={handleShare}
          className="mt-5 w-full py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15] text-white hover:brightness-110 transition disabled:opacity-60"
        >
          Поделиться номером
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={handleSave}
          className="mt-5 w-full py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15] text-white hover:brightness-110 transition disabled:opacity-60"
        >
          {loading ? 'Сохранение…' : 'Продолжить'}
        </button>
      </div>
    </div>
  );
};

const cyrillicOnly = (value) => String(value || '').replace(/[^а-яёА-ЯЁ\s\-]/g, '');

const FullNameRequiredScreen = ({ onSubmit }) => {
  const [fullName, setFullName] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleSave = async () => {
    const trimmedName = String(fullName || '').trim();
    if (!trimmedName) {
      setError('Введите ФИО на русском языке');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await onSubmit(trimmedName);
      try {
        localStorage.setItem(LS_FULL_NAME_KEY, trimmedName);
        window.dispatchEvent(new Event('japanexpress:full-name-updated'));
      } catch (_) {
        // ignore
      }
    } catch (e) {
      setError(e?.message || 'Ошибка сохранения');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg text-text-primary flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-stroke bg-surface p-6 backdrop-blur-md">
        <h1 className="text-xl font-semibold tracking-tight">Введите ваше ФИО</h1>
        <p className="mt-2 text-sm text-text-secondary">Укажите полное имя на русском языке для оформления заказов</p>

        <div className="mt-5">
          <input
            value={fullName}
            onChange={(e) => setFullName(cyrillicOnly(e.target.value))}
            placeholder="Иванов Иван Иванович"
            className="w-full rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none focus:border-white/25"
            autoComplete="name"
          />
          {error ? <div className="mt-2 text-xs text-red-300">{error}</div> : null}
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={handleSave}
          className="mt-5 w-full py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15] text-white hover:brightness-110 transition disabled:opacity-60"
        >
          {loading ? 'Сохранение…' : 'Продолжить'}
        </button>
      </div>
    </div>
  );
};

const CountryAddressRequiredScreen = ({ onSubmit }) => {
  const { initData } = useTelegram();
  const [address, setAddress] = React.useState('');
  const [country, setCountry] = React.useState(null);
  const [countryLoading, setCountryLoading] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setCountryLoading(false);
        return;
      }
      try {
        const r = await apiPost('/api/user/country/get', { initData: initDataRaw });
        if (!alive) return;
        setCountry(r?.country || 'KG');
      } catch (_) {
        if (!alive) return;
        setCountry('KG');
      } finally {
        if (alive) setCountryLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [initData?.initData]);

  const handleSave = async () => {
    if (!country) {
      setError('Выберите страну');
      return;
    }
    const trimmedAddress = String(address || '').trim();
    if (!trimmedAddress) {
      setError('Введите адрес проживания');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await onSubmit(trimmedAddress, country || 'KG');
      try {
        localStorage.setItem(LS_ADDRESS_KEY, trimmedAddress);
        window.dispatchEvent(new Event('japanexpress:address-updated'));
      } catch (_) {
        // ignore
      }
    } catch (e) {
      setError(e?.message || 'Ошибка сохранения');
      setLoading(false);
    }
  };

  if (countryLoading) {
    return (
      <div className="min-h-screen bg-bg text-text-primary flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-stroke bg-surface p-6 text-center backdrop-blur-md">
          <div className="text-sm text-text-secondary">Определение страны…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-text-primary flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-stroke bg-surface p-6 backdrop-blur-md">
        <h1 className="text-xl font-semibold tracking-tight">Страна и адрес</h1>
        <p className="mt-2 text-sm text-text-secondary">Укажите ваш адрес проживания для оформления заказов.</p>

        <div className="mt-4">
          <div className="text-xs text-text-muted mb-2">Выберите страну получения посылок</div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setCountry('KG')}
              className={`flex-1 py-4 rounded-2xl font-semibold text-base border-2 transition ${country === 'KG' ? 'border-neon-blue bg-neon-blue/15 text-text-primary shadow-[0_0_12px_rgba(59,130,246,0.3)]' : 'border-stroke bg-white/5 text-text-secondary hover:bg-white/8'}`}
            >
              🇰🇬<br/><span className="text-sm">Кыргызстан</span>
            </button>
            <button
              type="button"
              onClick={() => setCountry('KZ')}
              className={`flex-1 py-4 rounded-2xl font-semibold text-base border-2 transition ${country === 'KZ' ? 'border-neon-yellow bg-neon-yellow/15 text-text-primary shadow-[0_0_12px_rgba(250,204,21,0.3)]' : 'border-stroke bg-white/5 text-text-secondary hover:bg-white/8'}`}
            >
              🇰🇿<br/><span className="text-sm">Казахстан</span>
            </button>
          </div>
        </div>

        <div className="mt-4">
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={country === 'KZ' ? 'г. Алматы, ул. Примерная, д. 1, кв. 2' : 'г. Бишкек, ул. Примерная, д. 1, кв. 2'}
            className="w-full rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none focus:border-white/25"
            autoComplete="street-address"
          />
          {error ? <div className="mt-2 text-xs text-red-300">{error}</div> : null}
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={handleSave}
          className="mt-5 w-full py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15] text-white hover:brightness-110 transition disabled:opacity-60"
        >
          {loading ? 'Сохранение…' : 'Продолжить'}
        </button>
      </div>
    </div>
  );
};

const App = () => {
  // Task 8.0: AgreementScreen temporarily disabled — new agreement text for KZ not ready yet.
  // Keep the component in code for later re-enabling.
  const [showAgreement] = useState(false);

  const { isTelegram, initData } = useTelegram();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const isManagerHost = currentHost === 'test-manager.jpexgo.com' || currentHost === 'manager.jpexgo.com';
  const isPublicStandalonePath = !isTelegram && (['/cashier', '/pickup', '/admin/cashiers', '/admin/staff', '/manager/reports', '/manager/reconciliation'].some((path) => (
    currentPath === path || currentPath.startsWith(`${path}/`)
  )) || isManagerHost);

  const [phone, setPhone] = React.useState(null);
  const [inn, setInn] = React.useState(null);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isManager, setIsManager] = React.useState(false);
  const [isCourier, setIsCourier] = React.useState(false);

  const [fullName, setFullName] = React.useState(() => readCachedFullName());
  const [address, setAddress] = React.useState(() => readCachedAddress());
  const [userCountry, setUserCountry] = React.useState(null);

  const [fullNameChecked, setFullNameChecked] = React.useState(false);
  const [phoneChecked, setPhoneChecked] = React.useState(false);
  const [addressChecked, setAddressChecked] = React.useState(false);
  const [innChecked, setInnChecked] = React.useState(false);
  const [pendingNavigateTo, setPendingNavigateTo] = React.useState(null);

  const [lastRoute, setLastRoute] = React.useState(() => readLastRoute());

  React.useEffect(() => {
    const onPhoneUpdated = () => {
      try {
        const cached = localStorage.getItem('japanexpress_phone_v1');
        setPhone(cached ? String(cached) : '');
      } catch (_) {
        // ignore
      }
    };

    window.addEventListener('japanexpress:phone-updated', onPhoneUpdated);
    onPhoneUpdated();
    return () => window.removeEventListener('japanexpress:phone-updated', onPhoneUpdated);
  }, []);

  React.useEffect(() => {
    const onInnUpdated = () => {
      try {
        const cached = localStorage.getItem('japanexpress_inn_v1');
        setInn(cached ? String(cached) : '');
      } catch (_) {
        // ignore
      }
    };

    window.addEventListener('japanexpress:inn-updated', onInnUpdated);
    onInnUpdated();
    return () => window.removeEventListener('japanexpress:inn-updated', onInnUpdated);
  }, []);

  React.useEffect(() => {
    const onFullNameUpdated = () => {
      setFullName(readCachedFullName());
    };
    window.addEventListener('japanexpress:full-name-updated', onFullNameUpdated);
    onFullNameUpdated();
    return () => window.removeEventListener('japanexpress:full-name-updated', onFullNameUpdated);
  }, []);

  React.useEffect(() => {
    const onAddressUpdated = () => {
      setAddress(readCachedAddress());
    };
    window.addEventListener('japanexpress:address-updated', onAddressUpdated);
    onAddressUpdated();
    return () => window.removeEventListener('japanexpress:address-updated', onAddressUpdated);
  }, []);

  React.useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!isTelegram) return;
      if (showAgreement) return;
      if (innChecked) return;
      if (!addressChecked) return;

      const initDataRaw = initData?.initData;
      if (!initDataRaw) return;

      try {
        const r = await apiPost('/api/user/inn/get', { initData: initDataRaw });
        if (!alive) return;
        const serverInn = r?.inn || '';
        setInn(serverInn);
        try {
          const digits = digitsOnly(serverInn).slice(0, 14);
          if (digits) localStorage.setItem('japanexpress_inn_v1', digits);
          else localStorage.removeItem('japanexpress_inn_v1');
        } catch (_) {
          // ignore
        }
        setInnChecked(true);
      } catch (_) {
        if (!alive) return;
        setInn('');
        setInnChecked(true);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [initData?.initData, isTelegram, innChecked, showAgreement, addressChecked]);

  React.useEffect(() => {
    let alive = true;
    let timeoutId = null;

    const run = async () => {
      if (!isTelegram) return;
      if (showAgreement) return;
      if (phoneChecked) return;
      if (!fullNameChecked) return;

      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        timeoutId = setTimeout(() => {
          if (!alive) return;
          setPhoneChecked(true);
        }, 1200);
        return;
      }

      try {
        const r = await apiPost('/api/user/phone/get', { initData: initDataRaw });
        if (!alive) return;
        const serverPhone = r?.phone || '';
        setPhone(serverPhone);
        try {
          const digits = digitsOnly(serverPhone).slice(0, 12);
          if (digits) localStorage.setItem('japanexpress_phone_v1', digits);
          else localStorage.removeItem('japanexpress_phone_v1');
        } catch (_) {
          // ignore
        }
        setPhoneChecked(true);
      } catch (_) {
        if (!alive) return;
        setPhone('');
        setPhoneChecked(true);
      }
    };

    run();
    return () => {
      alive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [initData?.initData, isTelegram, phoneChecked, showAgreement, fullNameChecked]);

  React.useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!isTelegram) return;
      const initDataRaw = initData?.initData;
      if (!initDataRaw) return;
      try {
        const r = await apiPost('/api/roles/check', { initData: initDataRaw });
        if (!alive) return;
        setIsAdmin(!!r?.isAdmin);
        setIsManager(!!r?.isManager);
        setIsCourier(!!r?.isCourier);
      } catch (_) {
        if (!alive) return;
        setIsAdmin(false);
        setIsManager(false);
        setIsCourier(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [initData?.initData, isTelegram]);

  // Call /api/init to save agreed_at, username, first_name, last_name
  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!isTelegram) return;
      if (showAgreement) return;
      const initDataRaw = initData?.initData;
      if (!initDataRaw) return;
      try {
        await apiPost('/api/init', { initData: initDataRaw });
      } catch (_) {
        // ignore
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [initData?.initData, isTelegram, showAgreement]);

  // Fetch fullName from server — runs first (no dependency on other checked flags)
  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!isTelegram) return;
      if (showAgreement) return;
      if (fullNameChecked) return;
      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setFullNameChecked(true);
        return;
      }
      try {
        const r = await apiPost('/api/user/full-name/get', { initData: initDataRaw });
        if (!alive) return;
        const val = String(r?.fullName || '').trim();
        setFullName(val);
        try {
          if (val) localStorage.setItem(LS_FULL_NAME_KEY, val);
          else localStorage.removeItem(LS_FULL_NAME_KEY);
          window.dispatchEvent(new Event('japanexpress:full-name-updated'));
        } catch (_) {
          // ignore
        }
        setFullNameChecked(true);
      } catch (_) {
        if (!alive) return;
        setFullName('');
        try {
          localStorage.removeItem(LS_FULL_NAME_KEY);
          window.dispatchEvent(new Event('japanexpress:full-name-updated'));
        } catch (_) {
          // ignore
        }
        setFullNameChecked(true);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [initData?.initData, isTelegram, fullNameChecked, showAgreement]);

  // Fetch address from server — runs after phoneChecked
  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!isTelegram) return;
      if (showAgreement) return;
      if (addressChecked) return;
      if (!phoneChecked) return;
      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setAddressChecked(true);
        return;
      }
      try {
        const r = await apiPost('/api/user/address/get', { initData: initDataRaw });
        if (!alive) return;
        const val = String(r?.address || '').trim();
        setAddress(val);
        try {
          if (val) localStorage.setItem(LS_ADDRESS_KEY, val);
          else localStorage.removeItem(LS_ADDRESS_KEY);
          window.dispatchEvent(new Event('japanexpress:address-updated'));
        } catch (_) {
          // ignore
        }
        setAddressChecked(true);
      } catch (_) {
        if (!alive) return;
        setAddress('');
        try {
          localStorage.removeItem(LS_ADDRESS_KEY);
          window.dispatchEvent(new Event('japanexpress:address-updated'));
        } catch (_) {
          // ignore
        }
        setAddressChecked(true);
      }

      // Also load userCountry if user already onboarded
      try {
        const countryRes = await apiPost('/api/user/country/get', { initData: initDataRaw });
        if (!alive) return;
        if (countryRes?.country) setUserCountry(countryRes.country);
      } catch (_) {
        // ignore
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [initData?.initData, isTelegram, addressChecked, showAgreement, phoneChecked]);

  // Load userCountry independently — always runs after phone is checked
  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!isTelegram) return;
      if (!phoneChecked) return;
      const initDataRaw = initData?.initData;
      if (!initDataRaw) return;
      try {
        const countryRes = await apiPost('/api/user/country/get', { initData: initDataRaw });
        if (!alive) return;
        if (countryRes?.country) setUserCountry(countryRes.country);
      } catch (_) {
        // ignore
      }
    };
    run();
    return () => { alive = false; };
  }, [initData?.initData, isTelegram, phoneChecked]);

  // Listen for country changes from ProfilePage
  React.useEffect(() => {
    const onCountryUpdated = (e) => {
      const newCountry = e?.detail?.country;
      if (newCountry === 'KG' || newCountry === 'KZ') {
        setUserCountry(newCountry);
      }
    };
    window.addEventListener('japanexpress:country-updated', onCountryUpdated);
    return () => window.removeEventListener('japanexpress:country-updated', onCountryUpdated);
  }, []);

  const LastRouteTracker = () => {
    const location = useLocation();

    React.useEffect(() => {
      const skipRoutes = ['/cashier', '/pickup', '/admin/cashiers', '/admin/staff', '/admin/links', '/manager/reports', '/manager/reconciliation'];
      const shouldSkip = skipRoutes.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
      if (location.pathname && location.pathname !== '/' && !shouldSkip) {
        const next = normalizeRoute(location.pathname);
        setLastRoute(next);
        try {
          sessionStorage.setItem('japanexpress_last_route_v1', next);
        } catch (_) {
          // ignore
        }

        try {
          localStorage.setItem('japanexpress_last_route_v1', next);
        } catch (_) {
          // ignore
        }
      }
    }, [location.pathname]);

    return null;
  };

  if (isPublicStandalonePath) {
    return (
      <Router basename="/">
        <Routes>
          {isManagerHost ? <Route path="/" element={<Navigate to="/manager/reports" replace />} /> : null}
          <Route path="/cashier" element={<CashierPage />} />
          <Route path="/pickup" element={<PickupKioskPage />} />
          <Route path="/admin/cashiers" element={<AdminCashiersPage />} />
          <Route path="/admin/staff" element={<AdminStaffPage />} />
          <Route path="/manager/reports" element={<ManagerReportsPage />} />
          <Route path="/manager/reconciliation" element={<ManagerReconciliationPage />} />
        </Routes>
      </Router>
    );
  }

  if (!isTelegram) {
    return <TelegramOnlyScreen />;
  }

  if (showAgreement) {
    return <AgreementScreen onAccept={() => {}} />;
  }

  if (!fullNameChecked || !phoneChecked || !addressChecked || !innChecked) {
    return (
      <div className="min-h-screen bg-bg text-text-primary flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-stroke bg-surface p-6 text-center backdrop-blur-md">
          <div className="text-sm text-text-secondary">Загрузка…</div>
        </div>
      </div>
    );
  }

  const GateRedirect = ({ to, onDone }) => {
    const navigate = useNavigate();

    React.useEffect(() => {
      if (!to) return;
      navigate(to, { replace: true });
      if (onDone) onDone();
    }, [navigate, onDone, to]);

    return null;
  };

  const BackButtonManager = () => {
    const { tg } = useTelegram();
    const location = useLocation();
    const navigate = useNavigate();

    React.useEffect(() => {
      if (!tg?.BackButton) return;

      const isHome = location.pathname === '/';
      if (isHome) tg.BackButton.hide();
      else tg.BackButton.show();

      const onClick = () => navigate('/');
      tg.BackButton.onClick(onClick);

      return () => {
        tg.BackButton.offClick(onClick);
        tg.BackButton.hide();
      };
    }, [tg, location.pathname, navigate]);

    return null;
  };

  const AdminImportPage = () => {
    const { initData, tg } = useTelegram();
    const fileInputRef = React.useRef(null);
    const [file, setFile] = React.useState(null);
    const [fileSnapshot, setFileSnapshot] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [showImportChecksInfo, setShowImportChecksInfo] = React.useState(false);

    const [preview, setPreview] = React.useState(null);
    const [result, setResult] = React.useState(null);

    const [historyLoading, setHistoryLoading] = React.useState(false);
    const [historyError, setHistoryError] = React.useState('');
    const [historyItems, setHistoryItems] = React.useState([]);

    const [toolsLoading, setToolsLoading] = React.useState(false);
    const [toolsError, setToolsError] = React.useState('');
    const [toolsResult, setToolsResult] = React.useState('');
    const [usersStats, setUsersStats] = React.useState(null);

    const [exportCountry, setExportCountry] = React.useState('');

    const downloadUsersExcel = async () => {
      setToolsError('');
      setToolsResult('');

      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setToolsError('Telegram initData ещё не готов');
        return;
      }

      setToolsLoading(true);
      try {
        const bodyObj = { initData: initDataRaw };
        if (exportCountry) bodyObj.country = exportCountry;

        const res = await fetch(getApiPath('/api/admin/users/export/excel'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bodyObj),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.error || `Request failed: ${res.status}`);
        }

        const blob = await res.blob();
        const disposition = res.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?([^";]+)"?/i);
        const fileName = match?.[1] || 'japanexpress_users.xlsx';

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        setToolsResult('Файл Excel сформирован и скачан');
      } catch (e) {
        setToolsError(e?.message || 'Ошибка');
      } finally {
        setToolsLoading(false);
      }
    };

    const confirmAction = (message) =>
      new Promise((resolve) => {
        if (tg?.showPopup) {
          tg.showPopup(
            {
              title: 'Подтверждение',
              message: String(message || ''),
              buttons: [
                { id: 'cancel', type: 'cancel', text: 'Отмена' },
                { id: 'ok', type: 'destructive', text: 'Да' },
              ],
            },
            (buttonId) => resolve(buttonId === 'ok')
          );
          return;
        }
        resolve(window.confirm(String(message || '')));
      });

    const onDeleteHistoryItem = async (id) => {
      setHistoryError('');
      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setHistoryError('Telegram initData ещё не готов');
        return;
      }

      const ok = await confirmAction('Удалить эту запись из истории импортов?');
      if (!ok) return;

      try {
        await apiPost('/api/admin/import/history/delete', { initData: initDataRaw, id });
        loadHistory();
      } catch (e) {
        setHistoryError(e?.message || 'Ошибка удаления');
      }
    };

    const runTool = async (kind) => {
      setToolsError('');
      setToolsResult('');
      if (kind !== 'users') setUsersStats(null);

      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setToolsError('Telegram initData ещё не готов');
        return;
      }

      let confirmText = '';
      if (kind === 'clear') confirmText = 'Очистить базу заказов, историю статусов и историю импортов? Пользователи останутся.';
      if (kind === 'seed') confirmText = 'Заполнить базу тестовыми заказами?';
      if (kind === 'notify') confirmText = 'Запустить тест уведомлений для telegram_id=5274075051?';
      if (kind === 'manualDigest') confirmText = 'Отправить менеджерам сводку новых ручных треков сейчас?';
      if (kind === 'cashierSeed') confirmText = 'Создать или обновить тестового кассира с PIN 111?';
      if (kind === 'users') confirmText = '';

      if (confirmText) {
        const ok = await confirmAction(confirmText);
        if (!ok) return;
      }

      setToolsLoading(true);
      try {
        if (kind === 'clear') {
          const r = await apiPost('/api/admin/db/clear', { initData: initDataRaw });
          setToolsResult(`Очищено: orders=${r?.cleared?.orders ?? '-'}, history=${r?.cleared?.history ?? '-'}, imports=${r?.cleared?.imports ?? '-'}`);
          loadHistory();
        } else if (kind === 'seed') {
          const r = await apiPost('/api/admin/db/seed', { initData: initDataRaw });
          const tracks = Array.isArray(r?.seeded?.tracks) ? r.seeded.tracks.join(', ') : '';
          setToolsResult(`Добавлено тестовых заказов: ${r?.seeded?.inserted ?? '-'}${tracks ? ` (${tracks})` : ''}`);
        } else if (kind === 'notify') {
          const r = await apiPost('/api/admin/notifications/test', { initData: initDataRaw });
          setToolsResult(`Отправлено уведомлений: ${r?.sent ?? '-'} (трек ${r?.track || ''})`);
        } else if (kind === 'manualDigest') {
          const r = await apiPost('/api/admin/manual-tracks/digest-test', { initData: initDataRaw });
          const result = r?.result || {};
          setToolsResult(`Сводка ручных треков: треков=${result.tracks ?? '-'}, получателей=${result.recipients ?? '-'}, отправлено=${result.sent ?? '-'}, ошибок=${result.failed ?? '-'}`);
        } else if (kind === 'cashierSeed') {
          const r = await apiPost('/api/admin/cashiers/upsert-test', { initData: initDataRaw, fullName: 'Тестовый кассир', pin: '111' });
          setToolsResult(`Тестовый кассир готов: ${r?.cashier?.full_name || 'Тестовый кассир'}, PIN ${r?.pin || '111'}`);
        } else if (kind === 'users') {
          const r = await apiPost('/api/admin/users/stats', { initData: initDataRaw, limit: 100 });
          setUsersStats(r || null);
          setToolsResult(`Пользователей: ${r?.total ?? '-'} · С телефоном: ${r?.withPhone ?? '-'} · Без телефона: ${r?.withoutPhone ?? '-'} · KG: ${r?.countryKG ?? '-'} · KZ: ${r?.countryKZ ?? '-'}`);
        }
      } catch (e) {
        setToolsError(e?.message || 'Ошибка');
      } finally {
        setToolsLoading(false);
      }
    };

    const loadHistory = async () => {
      setHistoryError('');
      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setHistoryError('Telegram initData ещё не готов');
        return;
      }

      setHistoryLoading(true);
      try {
        const r = await apiPost('/api/admin/import/history', { initData: initDataRaw, limit: 10 });
        setHistoryItems(Array.isArray(r?.items) ? r.items : []);
      } catch (e) {
        setHistoryError(e?.message || 'Ошибка загрузки истории');
      } finally {
        setHistoryLoading(false);
      }
    };

    React.useEffect(() => {
      loadHistory();
    }, [initData?.initData]);

    const submitExcelFile = async ({ previewOnly = false, confirm = false } = {}) => {
      setError('');
      if (previewOnly) {
        setPreview(null);
        setResult(null);
      }

      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setError('Telegram initData ещё не готов. Попробуйте ещё раз через 1-2 секунды.');
        return;
      }
      if (!fileSnapshot && !(confirm && preview?.importToken)) {
        setError('Выберите Excel файл');
        return;
      }

      setLoading(true);
      try {
        const fd = new FormData();
        fd.append('initData', initDataRaw);
        if (confirm && preview?.importToken) {
          fd.append('importToken', preview.importToken);
        } else {
          fd.append('file', fileSnapshot.blob, fileSnapshot.name);
        }
        if (previewOnly) fd.append('preview', '1');
        if (confirm) fd.append('confirm', '1');

        const res = await fetch(getApiPath('/api/admin/import/excel'), {
          method: 'POST',
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (data?.preview) setPreview(data.preview);
          throw new Error(data?.error || `Request failed: ${res.status}`);
        }

        if (previewOnly) {
          setPreview(data?.preview || null);
        } else {
          setResult(data);
          setPreview(null);
          loadHistory();
        }
      } catch (e) {
        setError(e?.message || (previewOnly ? 'Ошибка проверки файла' : 'Ошибка импорта'));
      } finally {
        setLoading(false);
      }
    };

    const onPreview = () => submitExcelFile({ previewOnly: true });
    const onUpload = () => submitExcelFile({ confirm: true });

    const downloadImportCheckReport = async () => {
      setError('');
      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setError('Telegram initData ещё не готов');
        return;
      }
      if (!preview?.importToken) {
        setError('Сначала проверьте файл перед импортом');
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(getApiPath('/api/admin/import/check-report/excel'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ initData: initDataRaw, importToken: preview.importToken }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.error || `Request failed: ${res.status}`);
        }
        const blob = await res.blob();
        const disposition = res.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?([^";]+)"?/i);
        const fileName = match?.[1] || 'jpexgo_import_check.xlsx';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        setError(e?.message || 'Не удалось скачать отчёт проверки');
      } finally {
        setLoading(false);
      }
    };

    const previewWarnings = Array.isArray(preview?.warnings) ? preview.warnings : [];
    const warningGroups = [
      { key: 'unmatched_phone', title: 'Телефоны не найдены', count: preview?.unmatchedPhones?.length || 0 },
      { key: 'name_mismatch', title: 'ФИО отличается', count: preview?.nameMismatchWarnings?.length || 0 },
      { key: 'duplicate_phone_names', title: 'Дубли ФИО', count: preview?.duplicatePhoneNameWarnings?.length || 0 },
      { key: 'duplicate_tracks', title: 'Дубли треков', count: preview?.duplicateTrackWarnings?.length || 0 },
      { key: 'phone_change', title: 'Смена телефона', count: preview?.phoneChangeWarnings?.length || 0 },
      { key: 'status_rollback', title: 'Откат статуса', count: preview?.statusRollbackWarnings?.length || 0 },
      { key: 'received_status_change', title: 'Полученные', count: preview?.receivedStatusChangeWarnings?.length || 0 },
      { key: 'country_currency', title: 'Страна/валюта', count: preview?.countryCurrencyWarnings?.length || 0 },
      { key: 'ready_missing_values', title: 'Пустая сумма/вес', count: preview?.readyMissingValues?.length || 0 },
    ];

    return (
      <div className="min-h-screen bg-bg text-text-primary pb-20">
        <div className="px-4 pt-6 max-w-2xl mx-auto">
          <h1 className="text-xl font-semibold tracking-tight">Импорт Excel</h1>
          <p className="mt-2 text-sm text-text-secondary">Загрузка заказов и истории статусов из файла.</p>

          <div className="mt-5 rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Справка (для менеджера)</div>
            <div className="mt-3 text-sm text-text-secondary space-y-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Какие поля должны быть в Excel</div>
                <div className="mt-2 text-xs text-text-secondary">
                  Обязательное поле: <span className="text-text-primary font-semibold">Трек</span> (или <span className="text-text-primary font-semibold">Трек-номер</span>).
                  Остальные поля можно добавлять по необходимости: <span className="text-text-primary font-semibold">Статус</span>, <span className="text-text-primary font-semibold">Телефон</span> (или <span className="text-text-primary font-semibold">Номер телефона</span>), <span className="text-text-primary font-semibold">ФИО</span>, <span className="text-text-primary font-semibold">Дата</span>, <span className="text-text-primary font-semibold">Количество</span>, <span className="text-text-primary font-semibold">Вес</span>, <span className="text-text-primary font-semibold">Доллар</span>, <span className="text-text-primary font-semibold">Сом</span>, <span className="text-text-primary font-semibold">Тенге</span>.
                </div>
                <div className="mt-2 text-xs text-text-secondary">
                  <span className="text-text-primary font-semibold">Важно:</span> Для заказов KZ используйте колонку <span className="text-text-primary font-semibold">Тенге</span> вместо <span className="text-text-primary font-semibold">Сом</span>. Страна заказа определяется автоматически: если заполнено Тенге → KZ, если Сом → KG.
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Как работают уведомления по статусам</div>
                <div className="mt-2 text-xs text-text-secondary">
                  Уведомления отправляются при переходах статуса по цепочке:
                  <span className="text-text-primary font-semibold"> Поступил в Токио</span> → <span className="text-text-primary font-semibold">В Пути</span> → <span className="text-text-primary font-semibold">Готов к выдаче</span>.
                  Статус <span className="text-text-primary font-semibold">Готов к выдаче</span> - уведомление придёт всегда, даже если предыдущие статусы были пропущены или заказ загружен сразу с этим статусом.
                  Уведомление отправится тем пользователям, у которых уже привязан номер телефона в Mini App.
                  Перед импортом система покажет телефоны, которых нет среди пользователей, чтобы можно было проверить Excel до обновления базы.
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Отчёты</div>
                <div className="mt-2 text-xs text-text-secondary">
                  В админке доступны: <span className="text-text-primary font-semibold">Статистика пользователей</span> и <span className="text-text-primary font-semibold">Выгрузка пользователей в Excel</span>. Также есть <span className="text-text-primary font-semibold">История импортов</span> (последние 10 загрузок).
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Файл</div>
            <div className="mt-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={async (e) => {
                  const inputEl = e.currentTarget;
                  const selectedFile = e.target.files?.[0] || null;
                  setFile(selectedFile);
                  setFileSnapshot(null);
                  setPreview(null);
                  setResult(null);
                  setError('');
                  if (!selectedFile) return;
                  try {
                    const buffer = await selectedFile.arrayBuffer();
                    setFileSnapshot({
                      name: selectedFile.name,
                      blob: new Blob([buffer], { type: selectedFile.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                      lastModified: selectedFile.lastModified,
                      size: selectedFile.size,
                      capturedAt: Date.now(),
                    });
                  } catch (_) {
                    setError('Не удалось прочитать файл. Закройте Excel, выберите файл заново и попробуйте ещё раз.');
                  } finally {
                    if (inputEl) inputEl.value = '';
                  }
                }}
                className="block w-full text-sm text-text-secondary file:mr-4 file:rounded-xl file:border file:border-stroke file:bg-white/5 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-text-primary"
              />
              {file ? (
                <div className="mt-2 text-[11px] text-text-secondary">
                  {fileSnapshot
                    ? `Файл готов к проверке: ${file.name} · снимок ${new Date(fileSnapshot.capturedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
                    : 'Читаем файл…'}
                </div>
              ) : null}
            </div>

            {error ? <div className="mt-3 text-xs text-red-300">{error}</div> : null}

            <button
              type="button"
              disabled={loading}
              onClick={onPreview}
              className="mt-4 w-full py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15] text-white hover:brightness-110 transition disabled:opacity-60"
            >
              {loading ? 'Проверяем…' : 'Проверить файл перед импортом'}
            </button>

            <div className="mt-3 rounded-2xl border border-stroke bg-white/5 px-3 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Как теперь работать с импортом</div>
              <div className="mt-3 grid gap-2">
                {[
                  ['1', 'Выберите Excel и нажмите «Проверить файл перед импортом».'],
                  ['2', 'Посмотрите предупреждения. Если нужно, скачайте Excel-отчёт проверки.'],
                  ['3', 'Исправьте исходный Excel, сохраните файл и выберите его заново.'],
                  ['4', 'Снова проверьте файл. Если всё верно — нажмите «Импортировать».'],
                ].map(([step, text]) => (
                  <div key={step} className="flex items-start gap-2 text-[11px] leading-relaxed text-text-secondary">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/10 text-[10px] font-semibold text-text-primary">
                      {step}
                    </span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowImportChecksInfo(true)}
              className="mt-3 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-semibold text-text-primary hover:bg-white/10"
            >
              Какие проверки выполняются?
            </button>

            {showImportChecksInfo ? (
              <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-4 py-6">
                <div className="w-full max-w-lg rounded-3xl border border-stroke bg-bg p-5 shadow-2xl">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold text-text-primary">Проверки Excel перед импортом</div>
                      <div className="mt-1 text-xs text-text-secondary">Система ничего не записывает в базу на этапе проверки.</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowImportChecksInfo(false)}
                      className="h-8 w-8 rounded-full border border-stroke bg-white/5 text-text-secondary"
                    >
                      ×
                    </button>
                  </div>
                  <div className="mt-4 space-y-2 text-xs text-text-secondary">
                    {[
                      ['Телефоны', 'Показывает номера, которых нет среди пользователей Mini App.'],
                      ['ФИО', 'Сравнивает ФИО из Excel с ФИО в профиле клиента.'],
                      ['Дубли ФИО', 'Находит один телефон, указанный для разных ФИО.'],
                      ['Страна и валюта', 'Проверяет соответствие KG/сом и KZ/тенге.'],
                      ['Сумма и вес', 'Для статуса «Готов к выдаче» проверяет, заполнены ли сумма и вес.'],
                      ['Дубли треков', 'Находит один и тот же трек несколько раз внутри Excel.'],
                      ['Новые/обновляемые', 'Показывает, сколько заказов будет создано и сколько обновится.'],
                      ['Смена телефона', 'Предупреждает, если существующий трек переходит на другой телефон.'],
                      ['Откат статуса', 'Предупреждает, если статус возвращается на предыдущий этап.'],
                      ['Полученные заказы', 'Предупреждает, если Excel пытается изменить уже полученный заказ.'],
                    ].map(([title, text]) => (
                      <div key={title} className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                        <div className="font-semibold text-text-primary">{title}</div>
                        <div className="mt-1">{text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {preview ? (
              <div className={`mt-4 rounded-2xl border p-4 text-xs ${
                preview.errorCount
                  ? 'border-red-400/30 bg-red-400/10 text-red-100'
                  : previewWarnings.length
                    ? 'border-amber-400/30 bg-amber-400/10 text-amber-100'
                    : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-100'
              }`}>
                <div className="font-semibold">
                  {preview.errorCount
                    ? 'Есть ошибки, которые лучше исправить'
                    : previewWarnings.length
                      ? 'Есть предупреждения по файлу'
                      : 'Проверка пройдена'}
                </div>
                <div className="mt-2 text-text-secondary">
                  Строк к импорту: <span className="text-text-primary font-semibold">{preview.totalRows ?? 0}</span>
                  <br />
                  Телефонов в файле: <span className="text-text-primary font-semibold">{preview.phones ?? 0}</span>
                  <br />
                  Найдено пользователей: <span className="text-text-primary font-semibold">{preview.matchedPhones ?? 0}</span>
                  <br />
                  Новых заказов: <span className="text-text-primary font-semibold">{preview.newOrders ?? 0}</span>
                  <br />
                  Обновится существующих: <span className="text-text-primary font-semibold">{preview.existingOrders ?? 0}</span>
                  <br />
                  Предупреждений: <span className="text-text-primary font-semibold">{previewWarnings.length}</span>
                </div>
                {previewWarnings.length ? (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {warningGroups.filter((item) => item.count > 0).map((item) => (
                      <div key={item.key} className="rounded-xl border border-white/10 bg-black/10 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-text-muted">{item.title}</div>
                        <div className="mt-1 text-base font-semibold text-text-primary">{item.count}</div>
                      </div>
                    ))}
                  </div>
                ) : null}
                {preview.unmatchedPhones?.length ? (
                  <div className="mt-3 space-y-2">
                    <div className="rounded-2xl border border-red-400/25 bg-red-400/10 px-3 py-3">
                      <div className="flex items-start gap-2">
                        <svg className="mt-0.5 h-5 w-5 shrink-0 text-red-200" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div>
                          <div className="font-semibold text-red-50">Внимание перед импортом</div>
                          <div className="mt-1 text-[11px] leading-relaxed text-red-100/80">
                            Если вы исправили Excel после проверки, сохраните файл, выберите его заново и снова нажмите «Проверить файл перед импортом».
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="font-semibold text-red-100">
                      Проверьте эти телефоны в Excel перед импортом:
                    </div>
                    {preview.unmatchedPhones.slice(0, 10).map((item) => (
                      <div key={item.phone} className="rounded-xl border border-red-400/20 bg-black/10 px-3 py-2">
                        <div className="font-mono text-red-50">{item.phone}</div>
                        {item.fullName ? <div className="mt-1 text-[11px] text-red-100/75">{item.fullName}</div> : null}
                        <div className="mt-1 text-[11px] text-red-100/75">
                          Треки: {(item.tracks || []).slice(0, 5).join(', ')}
                          {(item.tracks || []).length > 5 ? ` и ещё ${(item.tracks || []).length - 5}` : ''}
                        </div>
                      </div>
                    ))}
                    {preview.unmatchedPhones.length > 10 ? (
                      <div className="text-[11px] text-red-100/75">И ещё {preview.unmatchedPhones.length - 10} телефон(ов)</div>
                    ) : null}
                  </div>
                ) : null}
                {previewWarnings.filter((item) => item.type !== 'unmatched_phone').length ? (
                  <div className="mt-3 space-y-2">
                    <div className="font-semibold text-text-primary">На что ещё обратить внимание:</div>
                    {previewWarnings.filter((item) => item.type !== 'unmatched_phone').slice(0, 8).map((item, idx) => (
                      <div key={`${item.type}-${item.phone}-${idx}`} className="rounded-xl border border-amber-400/20 bg-black/10 px-3 py-2">
                        <div className="font-semibold text-amber-50">{item.title}</div>
                        <div className="mt-1 text-[11px] text-amber-100/80">{item.message}</div>
                        <div className="mt-1 text-[11px] text-amber-100/70">
                          {item.phone ? `Телефон: ${item.phone}` : ''}
                          {item.fullName ? ` · ФИО: ${item.fullName}` : ''}
                        </div>
                        {item.tracks?.length ? (
                          <div className="mt-1 text-[11px] text-amber-100/70">
                            Треки: {item.tracks.slice(0, 5).join(', ')}
                            {item.tracks.length > 5 ? ` и ещё ${item.tracks.length - 5}` : ''}
                          </div>
                        ) : null}
                        <div className="mt-2 text-[11px] text-amber-50">{item.action}</div>
                      </div>
                    ))}
                    {previewWarnings.filter((item) => item.type !== 'unmatched_phone').length > 8 ? (
                      <div className="text-[11px] text-text-secondary">
                        И ещё {previewWarnings.filter((item) => item.type !== 'unmatched_phone').length - 8} предупреждений. Полный список есть в Excel-отчёте.
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {previewWarnings.length ? (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={downloadImportCheckReport}
                    className="mt-3 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-semibold text-text-primary hover:bg-white/10 disabled:opacity-60"
                  >
                    Скачать Excel-отчёт проверки
                  </button>
                ) : null}

                <button
                  type="button"
                  disabled={loading}
                  onClick={onUpload}
                  className={`mt-4 w-full py-3 rounded-2xl font-semibold text-sm transition disabled:opacity-60 ${
                    preview.unmatchedPhones?.length
                      ? 'border border-red-400/30 bg-red-400/15 text-red-50 hover:bg-red-400/20'
                      : 'bg-neon-yellow text-black hover:brightness-110'
                  }`}
                >
                  {loading
                    ? 'Импорт…'
                    : preview.unmatchedPhones?.length
                      ? 'Импортировать всё равно'
                      : 'Импортировать'}
                </button>
                {preview.unmatchedPhones?.length ? (
                  <div className="mt-2 text-[11px] leading-relaxed text-red-100/75">
                    Если импортировать всё равно, заказы сохранятся в базе с телефонами из Excel. Пользователи с ненайденными телефонами увидят эти заказы только после регистрации или привязки такого же телефона в Mini App, а уведомления сейчас не получат.
                  </div>
                ) : null}
              </div>
            ) : null}

            {result ? (
              <div className="mt-4 text-xs text-text-secondary">
                Обработано строк: <span className="text-text-primary font-semibold">{result?.rowsProcessed ?? '-'}</span>
                <br />
                Создано заказов: <span className="text-text-primary font-semibold">{result?.ordersInserted ?? '-'}</span>
                <br />
                Обновлено статусов: <span className="text-text-primary font-semibold">{result?.ordersUpdated ?? '-'}</span>
                <br />
                Добавлено в историю: <span className="text-text-primary font-semibold">{result?.historyInserted ?? '-'}</span>
                <br />
                Отправлено уведомлений: <span className="text-text-primary font-semibold">{result?.notifications?.sent ?? '-'}</span>
              </div>
            ) : null}
          </div>

          <div className="mt-4 rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">История импортов</div>
                <div className="mt-1 text-xs text-text-secondary">Последние 10 загрузок.</div>
              </div>
              <button
                type="button"
                disabled={historyLoading}
                onClick={loadHistory}
                className="h-9 px-4 rounded-2xl font-semibold text-xs border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60"
              >
                Обновить
              </button>
            </div>

            {historyLoading ? <div className="mt-3 text-xs text-text-secondary">Загрузка…</div> : null}
            {historyError ? <div className="mt-3 text-xs text-red-300">{historyError}</div> : null}

            {!historyLoading && !historyError && historyItems.length === 0 ? (
              <div className="mt-3 text-xs text-text-secondary">Пока нет импортов</div>
            ) : null}

            {!historyLoading && !historyError && historyItems.length > 0 ? (
              <div className="mt-4 space-y-2">
                {historyItems.map((it) => (
                  <div key={it.id} className="rounded-xl border border-stroke bg-white/3 px-3 py-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-xs text-text-primary font-semibold break-words">{it.file_name || 'Excel'}</div>
                      <button
                        type="button"
                        onClick={() => onDeleteHistoryItem(it.id)}
                        className="shrink-0 h-7 px-3 rounded-xl font-semibold text-[11px] border border-stroke bg-white/5 text-red-200 hover:bg-white/8 transition"
                      >
                        Удалить
                      </button>
                    </div>
                    <div className="mt-1 text-[11px] text-text-secondary">
                      {String(it.created_at || '')}
                      <br />
                      Строк: {it.rows_processed ?? '-'} · Создано: {it.orders_inserted ?? '-'} · Обновлено: {it.orders_updated ?? '-'} · История: {it.history_inserted ?? '-'}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="mt-4 rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Админ инструменты</div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => runTool('users')}
                disabled={toolsLoading}
                className="mt-3 w-full h-10 rounded-2xl font-semibold text-xs border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60"
              >
                Статистика пользователей
              </button>
              <button
                type="button"
                disabled={toolsLoading}
                onClick={downloadUsersExcel}
                className="mt-3 w-full h-10 rounded-2xl font-semibold text-xs border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60"
              >
                Выгрузить в Excel
              </button>
              <button
                type="button"
                onClick={() => runTool('notify')}
                disabled={toolsLoading}
                className="mt-3 w-full h-10 rounded-2xl font-semibold text-xs border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60"
              >
                Тест уведомлений
              </button>
              <button
                type="button"
                onClick={() => runTool('manualDigest')}
                disabled={toolsLoading}
                className="mt-3 w-full h-10 rounded-2xl font-semibold text-xs border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60"
              >
                Проверить ручные треки
              </button>
              <button
                type="button"
                onClick={() => runTool('cashierSeed')}
                disabled={toolsLoading}
                className="mt-3 w-full h-10 rounded-2xl font-semibold text-xs border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60"
              >
                Создать тестового кассира PIN 111
              </button>
              <div className="mt-2 w-full">
                <div className="text-[11px] text-text-muted mb-1">Фильтр по стране (для экспорта)</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setExportCountry('')}
                    className={`flex-1 h-8 rounded-xl text-xs font-semibold border transition ${exportCountry === '' ? 'border-white/25 bg-white/10 text-text-primary' : 'border-stroke bg-white/5 text-text-secondary hover:bg-white/8'}`}
                  >
                    Все
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportCountry('KG')}
                    className={`flex-1 h-8 rounded-xl text-xs font-semibold border transition ${exportCountry === 'KG' ? 'border-white/25 bg-white/10 text-text-primary' : 'border-stroke bg-white/5 text-text-secondary hover:bg-white/8'}`}
                  >
                    Кыргызстан (KG)
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportCountry('KZ')}
                    className={`flex-1 h-8 rounded-xl text-xs font-semibold border transition ${exportCountry === 'KZ' ? 'border-white/25 bg-white/10 text-text-primary' : 'border-stroke bg-white/5 text-text-secondary hover:bg-white/8'}`}
                  >
                    Казахстан (KZ)
                  </button>
                </div>
              </div>
            </div>
            {toolsLoading ? <div className="mt-3 text-xs text-text-secondary">Выполняю…</div> : null}
            {toolsError ? <div className="mt-3 text-xs text-red-300">{toolsError}</div> : null}
            {toolsResult ? <div className="mt-3 text-xs text-text-secondary">{toolsResult}</div> : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Router basename="/">
      <GateRedirect
        to={pendingNavigateTo}
        onDone={() => {
          setPendingNavigateTo(null);
        }}
      />
      <BackButtonManager />
      <LastRouteTracker />
      <Routes>
        <Route path="/cashier" element={<CashierPage />} />
        <Route path="/pickup" element={<PickupKioskPage />} />
        <Route path="/admin/cashiers" element={<AdminCashiersPage />} />
        <Route path="/admin/staff" element={<AdminStaffPage />} />
        <Route path="/manager/reports" element={<ManagerReportsPage />} />
        <Route path="/manager/reconciliation" element={<ManagerReconciliationPage />} />
        {!fullName ? (
          <Route
            path="*"
            element={
              <FullNameRequiredScreen
                onSubmit={async (nextFullName) => {
                  const initDataRaw = initData?.initData;
                  if (!initDataRaw) throw new Error('Telegram initData ещё не готов');

                  await apiPost('/api/user/full-name/set', { initData: initDataRaw, fullName: nextFullName });
                  setFullName(nextFullName);
                }}
              />
            }
          />
        ) : !phone ? (
          <Route
            path="*"
            element={
              <PhoneRequiredScreen
                initialPhone=""
                onSubmit={async (normalizedPhone) => {
                  const initDataRaw = initData?.initData;
                  if (!initDataRaw) throw new Error('Telegram initData ещё не готов');

                  await apiPost('/api/user/phone/set', { initData: initDataRaw, phone: normalizedPhone });
                  try {
                    localStorage.setItem('japanexpress_phone_v1', normalizedPhone);
                    window.dispatchEvent(new Event('japanexpress:phone-updated'));
                  } catch (_) {
                    // ignore
                  }
                }}
              />
            }
          />
        ) : !address ? (
          <Route
            path="*"
            element={
              <CountryAddressRequiredScreen
                onSubmit={async (nextAddress, detectedCountry) => {
                  const initDataRaw = initData?.initData;
                  if (!initDataRaw) throw new Error('Telegram initData ещё не готов');

                  await apiPost('/api/user/address/set', { initData: initDataRaw, address: nextAddress });
                  await apiPost('/api/user/country/set', { initData: initDataRaw, country: detectedCountry });

                  setAddress(nextAddress);
                  setUserCountry(detectedCountry);
                }}
              />
            }
          />
        ) : !inn ? (
          <Route
            path="*"
            element={
              <InnRequiredScreen
                initialInn=""
                onSubmit={async (normalizedInn) => {
                  const initDataRaw = initData?.initData;
                  if (!initDataRaw) throw new Error('Telegram initData ещё не готов');

                  await apiPost('/api/user/inn/set', { initData: initDataRaw, inn: normalizedInn });
                  try {
                    localStorage.setItem('japanexpress_inn_v1', normalizedInn);
                    window.dispatchEvent(new Event('japanexpress:inn-updated'));
                  } catch (_) {
                    // ignore
                  }
                }}
              />
            }
          />
        ) : (
          <>
            <Route path="/" element={<Home featuredPath={lastRoute || '/orders'} needsPhone={!phone} isAdmin={isAdmin} isManager={isManager} isCourier={isCourier} userCountry={userCountry} />} />
            <Route path="/orders" element={<OrdersPage userCountry={userCountry} />} />
            <Route path="/addresses" element={<AddressesPage userCountry={userCountry} />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/videos" element={<VideosPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/delivery" element={<DeliveryPage userCountry={userCountry} />} />
            <Route path="/courier" element={<CourierPage />} />
            <Route path="/manager/delivery" element={<ManagerDeliveryPage />} />
            <Route path="/manager/client-tracks" element={<ManagerClientTracksPage />} />
            <Route path="/manager/reports" element={<ManagerReportsPage />} />
            <Route path="/manager/reconciliation" element={<ManagerReconciliationPage />} />
            <Route path="/admin/cashiers" element={<AdminCashiersPage />} />
            <Route path="/admin/staff" element={<AdminStaffPage />} />
            <Route path="/admin/links" element={<AdminLinksPage />} />
            <Route path="/admin/import" element={<AdminImportPage />} />
          </>
        )}
      </Routes>
    </Router>
  );
};

export default App;

